import { createRoot } from "react-dom/client";
import { ThemeProvider } from "@material-ui/core/styles";
import { BrowserRouter } from "react-router-dom";
import { Provider } from "react-redux";
import { I18nextProvider } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import i18next from "i18next";

import { GrowthBookProvider } from "@growthbook/growthbook-react";
import store from "redux/store";
import "./index.css";
import 'react-step-progress-bar/styles.css';
import App from "./App";
import { setupBugsnag } from "integrations/bugsnag";
import { growthbook, initGrowthBook } from "integrations/growthbook";
import AppErrorBoundary from "components/errors/AppErrorBoundary";
import { installConsoleBuffer } from "diagnostics/consoleBuffer";

import translationDE from "./translations/de-DE.json";
import translationUS from "./translations/en-US.json";
import theme from 'config/theme';
import { anywize, marie } from "constants/constants";
import { STOP_STATUS_PALETTE } from "util/stopStatusUtils";

// Single source of truth: drive CSS variables for timeline dots from stopStatusUtils
Object.entries(STOP_STATUS_PALETTE).forEach(([key, value]) => {
  document.documentElement.style.setProperty(`--stop-status-${key}`, value);
});

const title = import.meta.env.VITE_TITLE || anywize;
document.title = title;

const favicon = document.getElementById('favicon');
if (title === marie) {
  favicon.href = '/marie_favicon.png'
}

const env = import.meta.env.VITE_ENV;
if (env && env !== 'production') {
  const envBadge = document.createElement('div');
  envBadge.style.position = 'fixed';
  envBadge.style.bottom = '20px';
  envBadge.style.left = '20px';
  envBadge.style.padding = '6px 12px';
  envBadge.style.borderRadius = '20px';
  envBadge.style.fontWeight = 'bold';
  envBadge.style.color = 'white';
  envBadge.style.zIndex = '9999';
  envBadge.style.display = 'flex';
  envBadge.style.alignItems = 'center';
  envBadge.style.gap = '6px';
  if (env === 'staging') {
    envBadge.style.backgroundColor = 'orange';
    envBadge.textContent = 'STAGING';
  } else if (env === 'development') {
    envBadge.style.backgroundColor = 'red';
    envBadge.textContent = 'DEVELOPMENT';
  } else {
    envBadge.style.backgroundColor = 'blue';
    envBadge.textContent = env.toUpperCase();
  }
  document.body.appendChild(envBadge);
}

i18next.use(LanguageDetector).init({
  interpolation: { escapeValue: false },
  fallbackLng: 'de',
  defaultNS: 'common',
  nsSeparator: false,
  keySeparator: false,
  detection: {
    order: ['localStorage', 'navigator', 'htmlTag'],
    caches: ['localStorage'],
    lookupLocalStorage: 'i18nextLng',
  },
  resources: {
    en: {
      common: translationUS,
    },
    de: {
      common: translationDE,
    },
  },
});

setupBugsnag();
installConsoleBuffer();
initGrowthBook();

createRoot(document.getElementById("root")).render(
  <Provider store={store}>
    <I18nextProvider i18n={i18next}>
      <BrowserRouter>
        <ThemeProvider theme={theme}>
          <GrowthBookProvider growthbook={growthbook}>
            <AppErrorBoundary>
              <App />
            </AppErrorBoundary>
          </GrowthBookProvider>
        </ThemeProvider>
      </BrowserRouter>
    </I18nextProvider>
  </Provider>
);
